import { z } from 'zod';
/**
 * Signal — one row in the *internal* signal feed (`/signals`). Distinct
 * from `ExternalSignal` (industry news from outside; lives at
 * `externalSignals/`) and from `Insight` (the agent's narrative
 * synthesis). A `Signal` is a graph-derived metric movement: a
 * scorecard measurable crossed a threshold, a Rock missed a milestone,
 * a person went quiet on Slack for N days, a customer's MRR dropped.
 *
 * The Alerts surface (`/alerts`) reads the same collection but filters
 * to `severity ∈ {critical, watch}`. Alerts is a stricter slice — same
 * data, sharper threshold.
 *
 * Multi-tenant: every doc carries `orgId`. Server-only writes from the
 * synthesis pipeline (Phase 5: real graph adapters; v0: deterministic
 * mocks for the seed). Clients only read.
 */
export const SIGNALS_COLLECTION = 'signals';
export const SignalKind = z.enum([
    /** A scorecard measurable crossed a threshold (above/below goal). */
    'scorecard.threshold',
    /** A Rock missed a milestone or fell behind pace. */
    'rock.slip',
    /** A watchlist customer crossed a metric (MRR drop, churn risk score). */
    'customer.metric',
    /** A person went quiet across connectors (Slack/Email/GitHub silence). */
    'person.quiet',
    /** A channel/thread died (no activity for N days). */
    'channel.quiet',
    /** Engineering: a PR has been blocked for >3 days. */
    'pr.blocked',
    /** Finance: revenue/burn anomaly. */
    'finance.anomaly',
    /** Catch-all — used when the synthesis pipeline emits a kind we don't
     * yet have a dedicated bucket for. */
    'other',
]);
/**
 * Severity matches `ExternalSignal` so the Alerts surface can compose
 * external + internal feeds without renaming the union. Mirrored from
 * `external-signal.ts` rather than imported to avoid a cross-file
 * coupling — the values are deliberately identical and the rename of one
 * shouldn't silently cascade.
 */
export const InternalSignalSeverity = z.enum(['info', 'watch', 'critical']);
export const Signal = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /** ISO timestamp of the originating event (when the metric crossed). */
    at: z.string(),
    /**
     * Display label for the entity the signal concerns (e.g. "Acme",
     * "@charlie", "PR #1184", "m-runway"). Renders inside `<EntityName>`
     * on the surface; the agent pipeline writes the resolution snapshot.
     */
    entity: z.string().min(1).max(200),
    kind: SignalKind,
    /**
     * Display name for the underlying metric (e.g. "MRR", "Days since
     * activity", "Coverage ratio"). Optional — `kind` already names the
     * shape, but `metric` lets the row read more naturally.
     */
    metric: z.string().max(120).optional(),
    /** Numeric value of the metric at signal time. */
    value: z.number().optional(),
    /** Threshold/goal that triggered the signal. */
    threshold: z.number().optional(),
    /** Severity. Drives status pill + Alerts vs Signals routing. */
    severity: InternalSignalSeverity.default('info'),
    /**
     * Optional href into the source surface (dossier slug, scorecard row,
     * PR url). Empty when the signal is descriptive only.
     */
    href: z.string().optional(),
    /** Free-form tags for faceting (`'churn'`, `'pipeline'`, `'people'`). */
    tags: z.array(z.string()).default([]),
});
/** Wire shape returned by `GET /api/orgs/:o/signals` and `/alerts`. */
export const ListSignalsResponse = z.object({
    signals: z.array(Signal),
});
